'use client';

import React, { useCallback } from 'react';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import {
  IResortPageInfo, IResortPageSnowInfo, IResortPathInfo, ISkiResortShop,
} from '@/api/resort/types';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import { PageBody } from '@/components/common/PageBody';
import { Main } from '@/components/resort/Layout/Main';
import { SeoSection } from '@/components/common/SeoSection';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { Hero400Error } from '@/components/400';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { ITASkiRentalReq } from '@/components/common/PlanYourTrip/types';
import { SkiRentalShop } from '@/components/resort/SkiRentalShop';
import { Text } from '@/components/ui/text';
import { PlaningViewsEnum } from '@/components/common/PlanYourTrip/constants';
import getSkiRentalUrl from '@/components/resort/SkiRentalShop/getSkiRentalUrl';

// Canonical responsive card grid, same as resort/cams. The cards carry
// no width — the columns live here. 2-up starts at md, not sm: in the
// 640-768px window a half-width card is ~282px, which is too narrow for
// the footer to hold the discount and the CTA on one line.
const offerListClass = 'grid grid-cols-1 gap-x-5 gap-y-7 md:grid-cols-2 xl:grid-cols-3';

export type ResortOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  shops: Array<ISkiResortShop>,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-skirentals-page';
export const TEMPLATE_ID = '3.2.7';
export const TEMPLATE_NAME = 'Resort Ski Rental';

export default function ResortOverviewPage({
  fullResort,
  shops,
  pathInfo,
}: ResortOverviewPageProps) {
  const { t } = useOTSTranslation('common');

  const baseSkiShop = (shops ?? []).find((sh) => `${sh.resortId}` === pathInfo.uuid);
  const anySkiShop = (shops ?? []).length ? shops[0] : undefined;
  const baseSkiUrl = (baseSkiShop || anySkiShop)?.url;

  // The rental form owns its own state and reports the whole thing on
  // every change, so this is a pure listener — nothing to mirror here.
  const handleChangeAccommodationParams = useCallback(
    (params: ITASkiRentalReq, submitted: boolean) => {
      if (submitted && baseSkiUrl) {
        const finalUrl = getSkiRentalUrl(baseSkiUrl, params);
        window.open(finalUrl, '_blank');
      }
    },
    [baseSkiUrl],
  );

  if (!fullResort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={t('resort.skiRentals.seoTitle', { resort: pathInfo.title })}>
            {t('resort.skiRentals.seoText', { resort: pathInfo.title })}
          </SeoSection>
          <PlanYourTrip
            initialTab={PlaningViewsEnum.rental}
            resort={pathInfo}
            hasSkiRental
            hasCarRental={pathInfo.hasCarRental}
            hasFlights={pathInfo.hasFlights}
            onSkiRentalChange={handleChangeAccommodationParams}
            rentalUrl={fullResort.rentalUrl}
            liftsUrl={fullResort.liftsUrl}
            lessonsUrl={fullResort.lessonsUrl}
            isPartner={fullResort.isPartner}
            resortCountry={fullResort?.region?.country_code}
          />
          {!(shops?.length) && (
            <Text variant="muted" className="text-center">
              {t('resort.skiRentals.noResults')}
            </Text>
          )}
          {!!(shops?.length) && (
            <div className={offerListClass}>
              {shops.map((shop) => <SkiRentalShop shop={shop} key={shop.id || shop.uuid} />)}
            </div>
          )}
          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
          />
        </PageBody>
      </Main>
    </>
  );
}

// fetchProps moved to ./skirentals.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
